import type { Run, Transform } from '@better-lights/core';
import type { Live } from './live.ts';

/**
 * Guides and handles (docs/spec/06 §4.5), drawn in screen space on a 2D canvas above the
 * WebGL canvas. Sizes are screen px; positions are image px × s + t.
 */
const ACCENT = '#FFB547';
export const LOUPE = 132;

export interface OverlayInput {
  runs: Run[];
  photoW: number;
  photoH: number;
  focusId: string | null;
  drawingId: string | null;
  tool: 'draw' | 'freehand' | 'select';
  guides: boolean;
}

function path(ctx: CanvasRenderingContext2D, pts: number[], t: Transform): void {
  ctx.beginPath();
  for (let i = 0; i < pts.length; i += 2) {
    const x = pts[i] * t.s + t.tx;
    const y = pts[i + 1] * t.s + t.ty;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
}

function strokeWith(ctx: CanvasRenderingContext2D, width: number, color: string): void {
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.stroke();
}

function disc(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  fill: string | null,
  stroke?: [number, string],
): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  if (stroke) strokeWith(ctx, stroke[0], stroke[1]);
}

export function flatPoints(run: Run, W: number, H: number): number[] {
  const out: number[] = [];
  for (const p of run.points) out.push(p.x * W, p.y * H);
  return out;
}

export function drawOverlay(ctx: CanvasRenderingContext2D, input: OverlayInput, live: Live, compare: boolean): void {
  const t = live.transform;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (input.guides && !compare) {
    const { runs, photoW: W, photoH: H } = input;
    // Other runs.
    for (const run of runs) {
      if (run.id === input.focusId) continue;
      const pts = flatPoints(run, W, H);
      if (input.tool === 'draw') {
        for (let i = 0; i < pts.length; i += 2) {
          disc(ctx, pts[i] * t.s + t.tx, pts[i + 1] * t.s + t.ty, 4.5, 'rgba(0,0,0,0.4)', [
            1.5,
            'rgba(255,255,255,0.85)',
          ]);
        }
      } else if (pts.length >= 4) {
        path(ctx, pts, t);
        strokeWith(ctx, 5, 'rgba(0,0,0,0.45)');
        strokeWith(ctx, 1.5, 'rgba(255,255,255,0.85)');
      }
    }
    // Freehand stroke in progress.
    if (live.stroke && live.stroke.length > 1) {
      path(
        ctx,
        live.stroke.flatMap((p) => [p.x, p.y]),
        t,
      );
      strokeWith(ctx, 5, 'rgba(0,0,0,0.45)');
      strokeWith(ctx, 2, 'rgba(255,181,71,0.95)');
    }
    // Focused run, from the live drag copy when dragging.
    const focus = runs.find((r) => r.id === input.focusId);
    const drawing = input.drawingId !== null && input.drawingId === input.focusId;
    if (focus) {
      const pts = live.drag && live.drag.runId === focus.id ? live.drag.live : flatPoints(focus, W, H);
      const n = pts.length / 2;
      if (drawing && live.hover && n >= 1) {
        ctx.beginPath();
        ctx.moveTo(pts[pts.length - 2] * t.s + t.tx, pts[pts.length - 1] * t.s + t.ty);
        ctx.lineTo(live.hover.x * t.s + t.tx, live.hover.y * t.s + t.ty);
        strokeWith(ctx, 1.5, 'rgba(255,181,71,0.75)');
      }
      if (n >= 2) {
        path(ctx, pts, t);
        strokeWith(ctx, 5, 'rgba(0,0,0,0.5)');
        strokeWith(ctx, 2, ACCENT);
      }
      if (!drawing && !live.drag) {
        for (let i = 2; i < pts.length; i += 2) {
          const mx = ((pts[i - 2] + pts[i]) / 2) * t.s + t.tx;
          const my = ((pts[i - 1] + pts[i + 1]) / 2) * t.s + t.ty;
          disc(ctx, mx, my, 4.5, 'rgba(0,0,0,0.55)', [1.5, 'rgba(255,255,255,0.9)']);
        }
      }
      if (drawing && n >= 2) {
        disc(ctx, pts[pts.length - 2] * t.s + t.tx, pts[pts.length - 1] * t.s + t.ty, 13, null, [2, ACCENT]);
      }
      for (let i = 0; i < n; i++) {
        const x = pts[i * 2] * t.s + t.tx;
        const y = pts[i * 2 + 1] * t.s + t.ty;
        const dragged = live.drag && live.drag.runId === focus.id && live.drag.index === i;
        disc(ctx, x, y, dragged ? 12 : 8, '#FFFFFF');
        disc(ctx, x, y, 4.5, ACCENT);
      }
    }
    if (live.hover && input.tool === 'draw' && !live.drag) {
      disc(ctx, live.hover.x * t.s + t.tx, live.hover.y * t.s + t.ty, 6, 'rgba(255,255,255,0.35)', [1.5, '#FFFFFF']);
    }
  }
  const loupe = loupeFrame(live);
  if (loupe && live.drag) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(loupe.cx, loupe.cy, loupe.radius, 0, Math.PI * 2);
    ctx.clip();
    const k = loupe.scale;
    const lt = { s: k, tx: loupe.cx - loupe.ix * k, ty: loupe.cy - loupe.iy * k };
    path(ctx, live.drag.live, lt);
    strokeWith(ctx, 1.5, ACCENT);
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(loupe.cx - 10, loupe.cy);
    ctx.lineTo(loupe.cx + 10, loupe.cy);
    ctx.moveTo(loupe.cx, loupe.cy - 10);
    ctx.lineTo(loupe.cx, loupe.cy + 10);
    ctx.stroke();
    ctx.restore();
    disc(ctx, loupe.cx, loupe.cy, loupe.radius, null, [3, 'rgba(255,255,255,0.92)']);
  }
}

/** §4.6 loupe placement: top-left, or top-right when the finger is in the top-left area. */
export function loupeFrame(
  live: Live,
): { cx: number; cy: number; radius: number; ix: number; iy: number; scale: number } | null {
  if (!live.drag || !live.loupeAt) return null;
  const r = LOUPE / 2;
  const left = live.loupeAt.sx < live.viewW / 2 && live.loupeAt.sy < LOUPE + 40;
  const cx = left ? live.viewW - 16 - r : 16 + r;
  const i = live.drag.index;
  return {
    cx,
    cy: 16 + r,
    radius: r,
    ix: live.drag.live[i * 2],
    iy: live.drag.live[i * 2 + 1],
    scale: 3 * live.transform.s,
  };
}
